import { useId, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { LwButton, LwModal } from '@/components/lw';
import { stopAppraisalFeeAlerts, type AppraisalFeeAlertStop } from '@/services/appraisalFeeComplianceService';

export const STOP_REASON_MIN = 10;
export const STOP_REASON_MAX = 500;

/**
 * Reason prompt for stopping a loan's invoice fee alerts. The finding stays in the report;
 * only the emails and the morning digest stop. The reason is saved with the user and time.
 */
export function StopFeeAlertsDialog({ loanId, loanNumber, onClose, onStopped }: {
  loanId: string;
  loanNumber: string;
  onClose: () => void;
  onStopped: (result: AppraisalFeeAlertStop) => void;
}) {
  const id = useId();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const length = reason.trim().replace(/\s+/g, ' ').length;
  const valid = length >= STOP_REASON_MIN && length <= STOP_REASON_MAX;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) {
      setError(valid ? null : `Enter a reason between ${STOP_REASON_MIN} and ${STOP_REASON_MAX} characters.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await stopAppraisalFeeAlerts(loanId, reason.trim());
      toast.success(`Alerts stopped for ${loanNumber}. The finding stays in the report.`, { position: 'top-right' });
      onStopped(result);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Alerts could not be stopped. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <LwModal eyebrow="Fee compliance" title={`Stop alerts for ${loanNumber}`}
      sub="Use this when the fee is updated in Encompass but cannot be redisclosed. Overage emails and the morning digest stop for this loan; the finding stays visible in the report until it clears."
      onClose={onClose}
      footer={<>
        <LwButton variant="sm" type="button" onClick={onClose} disabled={submitting}>Cancel</LwButton>
        <LwButton variant="sm-solid" type="submit" form={`${id}-form`} aria-disabled={!valid || submitting} aria-busy={submitting}>
          {submitting && <Loader2 className="animate-spin" size={14} aria-hidden="true" />}Stop alerts
        </LwButton>
      </>}>
      <form id={`${id}-form`} onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor={`${id}-reason`}>Reason <span className="req" aria-hidden="true">*</span><span className="sr-only">(required)</span></label>
          <textarea id={`${id}-reason`} value={reason} onChange={event => setReason(event.target.value)} required aria-required="true"
            aria-describedby={`${id}-hint${error ? ` ${id}-error` : ''}`} aria-invalid={error ? 'true' : undefined}
            maxLength={STOP_REASON_MAX} rows={4} autoComplete="off" />
          <span id={`${id}-hint`} className="hint">{STOP_REASON_MIN} to {STOP_REASON_MAX} characters. Saved with your name and the time. {length}/{STOP_REASON_MAX}</span>
          {error && <span id={`${id}-error`} className="err" role="alert">{error}</span>}
        </div>
      </form>
    </LwModal>
  );
}
